"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Scheduler = void 0;
const lodash_1 = require("lodash");
const luxon_1 = require("luxon");
class Scheduler {
    constructor() {
        this.daysOfWeek = [
            'Sunday',
            'Monday',
            'Tuesday',
            'Wednesday',
            'Thursday',
            'Friday',
            'Saturday'
        ];
    }
    convertScheduleSpecificDateToInterval(schedule) {
        const interval = { from: null, to: null };
        const fromDateTime = `${schedule.date.toISODate()}T${schedule.from.toLocaleString(luxon_1.DateTime.TIME_24_SIMPLE)}`;
        const toDateTime = `${schedule.date.toISODate()}T${schedule.to.toLocaleString(luxon_1.DateTime.TIME_24_SIMPLE)}`;
        interval.from = luxon_1.DateTime.fromISO(fromDateTime);
        interval.to = luxon_1.DateTime.fromISO(toDateTime);
        return interval;
    }
    validateAndCastScheduleSpecificDate(schedule, propertyName) {
        const s = {
            date: luxon_1.DateTime.fromISO(schedule.date),
            from: luxon_1.DateTime.fromISO(schedule.from),
            to: luxon_1.DateTime.fromISO(schedule.to)
        };
        if (s.date.invalidReason) {
            throw new Error(`${propertyName} "date" must be a date in the format YYYY-MM-DD`);
        }
        if (s.from.invalidReason) {
            throw new Error(`${propertyName} "from" must be a time in the format HH:mm`);
        }
        else if (s.to.invalidReason) {
            throw new Error(`${propertyName} "to" must be a time in the format HH:mm`);
        }
        else if (!this.isTimeAfter(s.to, s.from)) {
            throw new Error(`${propertyName} "to" must be greater than "from"`);
        }
        return s;
    }
    validateAndCastDaySchedule(schedule) {
        const s = {
            from: luxon_1.DateTime.fromISO(schedule.from),
            to: luxon_1.DateTime.fromISO(schedule.to),
            reference: schedule.reference || null
        };
        if (s.from.invalidReason) {
            throw new Error('"from" must be a time in the format HH:mm');
        }
        else if (s.to.invalidReason) {
            throw new Error('"to" must be a time in the format HH:mm');
        }
        else if (!this.isTimeAfter(s.to, s.from)) {
            throw new Error('"to" must be greater than "from"');
        }
        if (schedule.unavailability) {
            s.unavailability = [];
            for (const unavailability of schedule.unavailability) {
                const breakPeriod = {
                    from: luxon_1.DateTime.fromISO(unavailability.from),
                    to: luxon_1.DateTime.fromISO(unavailability.to),
                    reference: unavailability.reference || null
                };
                if (breakPeriod.from.invalidReason) {
                    throw new Error('unavailability "from" must be a time in the format HH:mm');
                }
                else if (breakPeriod.to.invalidReason) {
                    throw new Error('unavailability "to" must be a time in the format HH:mm');
                }
                else if (!this.isTimeAfter(breakPeriod.to, breakPeriod.from)) {
                    throw new Error('unavailability "to" must be greater than "from"');
                }
                s.unavailability.push(breakPeriod);
            }
        }
        return s;
    }
    // tslint:disable-next-line:max-func-body-length
    validateAndCastParams(params) {
        const p = (0, lodash_1.cloneDeep)(params);
        if (!p.from) {
            throw new Error('from is required');
        }
        else if (!p.to) {
            throw new Error('to is required');
        }
        p.from = luxon_1.DateTime.fromISO(params.from);
        p.to = luxon_1.DateTime.fromISO(params.to);
        // console.log(`params.from is ${params.from} meaning ${p.from.toHTTP()} and p.from.invalidReason ${p.from.invalidReason}`)
        // console.log(`params.to is ${params.to} meaning ${p.to.toHTTP()} and p.to.invalidReason ${p.to.invalidReason}`)
        if (p.from.invalidReason) {
            throw new Error('"from" must be a date in the format YYYY-MM-DD');
        }
        else if (p.to.invalidReason) {
            throw new Error('"to" must be a date in the format YYYY-MM-DD');
        }
        else if (!this.isDateTimeAfter(p.to, p.from)) {
            throw new Error('"to" must be greater than "from"');
        }
        if (!p.schedule) {
            throw new Error('schedule is required');
        }
        for (const dayName of this.daysOfWeek) {
            if (p.schedule[dayName] !== undefined) {
                try {
                    p.schedule[dayName] = this.validateAndCastDaySchedule(p.schedule[dayName]);
                }
                catch (err) {
                    err.message = `${dayName}: ${err.message}`;
                    throw err;
                }
            }
        }
        if (p.schedule.weekdays) {
            try {
                p.schedule.weekdays = this.validateAndCastDaySchedule(p.schedule.weekdays);
            }
            catch (err) {
                err.message = `weekdays: ${err.message}`;
                throw err;
            }
        }
        if (p.schedule.unavailability) {
            for (let i = 0; i < p.schedule.unavailability.length; i += 1) {
                const unavailability = p.schedule.unavailability[i];
                let interval = { from: null, to: null };
                if (unavailability.date !== undefined) {
                    const s = this.validateAndCastScheduleSpecificDate(unavailability, 'unavailability');
                    interval = this.convertScheduleSpecificDateToInterval(s);
                }
                else {
                    interval.from = luxon_1.DateTime.fromISO(unavailability.from);
                    interval.to = luxon_1.DateTime.fromISO(unavailability.to);
                    if (interval.from.invalidReason) {
                        throw new Error('unavailability "from" must be a date in the format YYYY-MM-DDTHH:mm');
                    }
                    else if (interval.to.invalidReason) {
                        throw new Error('unavailability "to" must be a date in the format YYYY-MM-DDTHH:mm');
                    }
                    else if (interval.to < interval.from) {
                        throw new Error('unavailability "to" must be greater than "from"');
                    }
                }
                p.schedule.unavailability[i] = interval;
            }
        }
        if (p.schedule.allocated) {
            for (const allocated of p.schedule.allocated) {
                allocated.from = luxon_1.DateTime.fromISO(allocated.from);
                if (allocated.from.invalidReason) {
                    throw new Error('"allocated.from" must be a date in the format YYYY-MM-DDTHH:mm');
                }
                if (isNaN(allocated.duration)) {
                    throw new Error('"allocated.duration" must be a positive integer');
                }
                allocated.duration = Math.floor(allocated.duration);
                if (allocated.duration <= 0) {
                    throw new Error('"allocated.duration" must be a positive integer');
                }
                const allocatedMinutes = p.interval * Math.ceil(allocated.duration / p.interval);
                allocated.to = allocated.from.plus({ minutes: allocatedMinutes });
            }
        }
        if (p.schedule.custom_schedule) {
            p.schedule.custom_schedule.forEach((customSchedule, key) => {
                p.schedule.custom_schedule[key] = this.validateAndCastScheduleSpecificDate(customSchedule, 'custom_schedule');
            });
        }
        if (isNaN(p.interval)) {
            throw new Error('"interval" must be a positive integer');
        }
        p.interval = Math.floor(p.interval);
        if (p.interval <= 0) {
            throw new Error('"interval" must be a positive integer');
        }
        if (isNaN(p.duration)) {
            throw new Error('"duration" must be a positive integer');
        }
        p.duration = Math.floor(p.duration);
        if (p.duration <= 0) {
            throw new Error('"duration" must be a positive integer');
        }
        this.params = p;
    }
    isTimeBefore(first, second) {
        if (first.hour < second.hour) {
            return true;
        }
        else if (first.hour === second.hour) {
            return first.minute < second.minute;
        }
        return false;
    }
    isTimeSameOrAfter(first, second) {
        if (first.hour > second.hour) {
            return true;
        }
        else if (first.hour === second.hour) {
            return first.minute >= second.minute;
        }
        return false;
    }
    isTimeAfter(first, second) {
        if (first.hour > second.hour) {
            return true;
        }
        else if (first.hour === second.hour) {
            return first.minute > second.minute;
        }
        return false;
    }
    isDateTimeBefore(first, second) {
        if (first.year < second.year) {
            return true;
        }
        else if (first.year === second.year) {
            if (first.ordinal < second.ordinal) {
                return true;
            }
            else if (first.ordinal === second.ordinal) {
                return this.isTimeBefore(first, second);
            }
        }
        return false;
    }
    isDateTimeSameOrAfter(first, second) {
        if (first.year > second.year) {
            return true;
        }
        else if (first.year === second.year) {
            if (first.ordinal > second.ordinal) {
                return true;
            }
            else if (first.ordinal === second.ordinal) {
                return this.isTimeSameOrAfter(first, second);
            }
        }
        return false;
    }
    isDateTimeAfter(first, second) {
        if (first.year > second.year) {
            return true;
        }
        else if (first.year === second.year) {
            if (first.ordinal > second.ordinal) {
                return true;
            }
            else if (first.ordinal === second.ordinal) {
                return this.isTimeAfter(first, second);
            }
        }
        return false;
    }
    isTimeslotAvailable(timeSlotStart, timeSlotEnd, allocateds) {
        for (const allocated of allocateds) {
            if (this.isTimeSameOrAfter(timeSlotStart, allocated.from) && this.isTimeBefore(timeSlotStart, allocated.to)) {
                return false;
            }
            else if (this.isTimeBefore(allocated.from, timeSlotEnd) && this.isTimeAfter(allocated.to, timeSlotStart)) {
                return false;
            }
        }
        return true;
    }
    isDateTimeslotAvailable(dateTimeSlotStart, dateTimeSlotEnd, allocateds) {
        for (const allocated of allocateds) {
            if (this.isDateTimeSameOrAfter(dateTimeSlotStart, allocated.from) && this.isDateTimeBefore(dateTimeSlotStart, allocated.to)) {
                return false;
            }
            else if (this.isDateTimeBefore(allocated.from, dateTimeSlotEnd) && this.isDateTimeAfter(allocated.to, dateTimeSlotStart)) {
                return false;
            }
        }
        return true;
    }
    getScheduleForDay(day) {
        const weekdayName = day.toFormat('cccc');
        let customSchedule = undefined;
        // console.log(`day is ${day.toHTTP()} and custom is ${JSON.stringify(this.params.schedule.custom_schedule)}`)
        if (this.params.schedule.custom_schedule !== undefined) {
            const customScheduleForThisDay = this.params.schedule.custom_schedule.filter((cs) => cs.date.toMillis() === day.toMillis());
            /*
            for (var item of this.params.schedule.custom_schedule) {
                console.log(`special day is ${(<DateTime> item.date).toHTTP()} and we are ${day.toHTTP()} same ? ${(<DateTime> item.date).toMillis() === day.toMillis()}`)
            }
            */
            if (customScheduleForThisDay.length) {
                customSchedule = {
                    from: customScheduleForThisDay[0].from,
                    to: customScheduleForThisDay[0].to
                };
            }
        }
        if (this.params.schedule[weekdayName] !== undefined) {
            if (customSchedule !== undefined) {
                customSchedule.unavailability = this.params.schedule[weekdayName].unavailability || [];
                return customSchedule;
            }
            return this.params.schedule[weekdayName];
        }
        else if (weekdayName !== 'Saturday' && weekdayName !== 'Sunday' && this.params.schedule.weekdays !== undefined) {
            if (customSchedule !== undefined) {
                customSchedule.unavailability = this.params.schedule.weekdays.unavailability || [];
                return customSchedule;
            }
            return this.params.schedule.weekdays;
        }
        return undefined;
    }
    getAvailability(p) {
        this.validateAndCastParams(p);
        const response = {};
        var curDate = luxon_1.DateTime.fromISO(this.params.from);
        // Loop on each day from <curDate> to <toDate>
        while (curDate < this.params.to) {
            const daySchedule = this.getScheduleForDay(curDate);
            // We have a schedule for this day
            if (daySchedule !== undefined) {
                const dayAvailability = [];
                var timeSlotStart = daySchedule.from.set({ year: curDate.year, ordinal: curDate.ordinal });
                // Loop from <curTime> to <endTime> in <interval> increments
                while (this.isTimeBefore(timeSlotStart, daySchedule.to)) {
                    const timeSlotEnd = timeSlotStart.plus({ minutes: this.params.duration });
                    if (this.isTimeAfter(timeSlotEnd, daySchedule.to)) {
                        dayAvailability.push({
                            time: timeSlotStart.toLocaleString(luxon_1.DateTime.TIME_24_SIMPLE),
                            available: false,
                            reference: daySchedule.reference
                        });
                        timeSlotStart = timeSlotStart.plus({ minutes: this.params.interval });
                        continue;
                    }
                    let isAvailable = true;
                    // Verify that the resource is not unavailable for the <curTime>
                    if (this.params.schedule.unavailability !== undefined) {
                        isAvailable = this.isDateTimeslotAvailable(timeSlotStart, timeSlotEnd, this.params.schedule.unavailability);
                    }
                    // Verify that the resource is not on a daily break
                    if (isAvailable && daySchedule.unavailability !== undefined) {
                        isAvailable = this.isTimeslotAvailable(timeSlotStart, timeSlotEnd, daySchedule.unavailability);
                    }
                    // Verify that the resource is not allocated for the <curTime>
                    if (isAvailable && this.params.schedule.allocated) {
                        const allocatedToday = this.params.schedule.allocated.filter((a) => {
                            return a.from.year === timeSlotStart.year &&
                                a.from.ordinal === timeSlotStart.ordinal;
                        });
                        isAvailable = this.isTimeslotAvailable(timeSlotStart, timeSlotEnd, allocatedToday);
                    }
                    dayAvailability.push({
                        time: timeSlotStart.toLocaleString(luxon_1.DateTime.TIME_24_SIMPLE),
                        available: isAvailable,
                        reference: daySchedule.reference
                    });
                    timeSlotStart = timeSlotStart.plus({ minutes: this.params.interval });
                }
                response[curDate.toISODate()] = dayAvailability;
            }
            curDate = curDate.plus({ days: 1 });
        }
        return response;
    }
    getIntersection(p) {
        const params = Object.assign({ schedule: null }, (0, lodash_1.cloneDeep)((0, lodash_1.omit)(p, ['schedules'])));
        const availabilities = [];
        for (const schedule of p.schedules) {
            params.schedule = schedule;
            availabilities.push(this.getAvailability(params));
        }
        if (availabilities.length === 0) {
            return {};
        }
        else if (availabilities.length === 1) {
            return availabilities[0];
        }
        for (const day of Object.keys(availabilities[0])) {
            availabilities[0][day] = availabilities[0][day].map((timeAv, idx) => {
                if (timeAv.available) {
                    timeAv.available = availabilities.length === availabilities.filter((days) => {
                        if (days[day] === undefined) {
                            return false;
                        }
                        return 1 === days[day].filter((time) => {
                            return timeAv.time === time.time && time.available;
                        }).length;
                    }).length;
                }
                return timeAv;
            });
        }
        return availabilities[0];
    }
}
exports.Scheduler = Scheduler;
//# sourceMappingURL=Scheduler.js.map